import { useState, useMemo, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { 
  Copy, TrendingUp, Wallet, MousePointer, CheckCircle, Clock, 
  Link as LinkIcon, Eye, EyeOff, LogOut, ArrowUpRight, 
  DollarSign, Target, Zap, Calendar, BarChart3, ExternalLink,
  CreditCard, Award
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";

interface Affiliate {
  id: string; email: string; name: string; referral_code: string;
  commission_rate: number; total_earnings: number; pending_earnings: number;
  is_active: boolean; created_at: string;
}

interface Conversion {
  id: string; affiliate_id: string; order_id: string | null; order_amount: number;
  commission_amount: number; status: string; referral_code: string;
  created_at: string; updated_at: string;
}

const EDGE_FUNCTION_URL = "https://tpujpzojoqwytuhgbwtw.supabase.co/functions/v1/affiliate-auth";

const AffiliateDashboard = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [affiliate, setAffiliate] = useState<Affiliate | null>(null);
  const [conversions, setConversions] = useState<Conversion[]>([]);
  const [clickCount, setClickCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Validate session via secure edge function (cookie-based)
  const validateSession = async (): Promise<boolean> => {
    try {
      const response = await fetch(`${EDGE_FUNCTION_URL}/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      
      const data = await response.json();
      
      if (data.valid && data.affiliate) {
        setAffiliate(data.affiliate);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error("Session validation error:", error);
      return false;
    }
  };

  // Fetch affiliate data (clicks + conversions) via edge function
  const fetchAffiliateData = async () => {
    try {
      const response = await fetch(`${EDGE_FUNCTION_URL}/get-data`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      
      const data = await response.json();
      setClickCount(data.clickCount || 0);
      setConversions(data.conversions || []);
    } catch (error) {
      console.error("Error fetching affiliate data:", error);
    }
  };

  // Restore session on mount
  const restoreSession = async () => {
    try {
      const isValid = await validateSession();
      
      if (isValid) {
        setIsLoggedIn(true);
        await fetchAffiliateData();
      }
    } catch (error) {
      console.error("Session restore error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    restoreSession();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch(`${EDGE_FUNCTION_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        toast.error(data.error || "Erreur de connexion.");
        setIsLoading(false);
        return;
      }
      
      setAffiliate(data.affiliate);
      setIsLoggedIn(true);
      
      // Fetch additional data
      await fetchAffiliateData();
      
      toast.success("Connexion réussie !");
    } catch (error) { 
      console.error(error); 
      toast.error("Erreur de connexion."); 
    } finally { 
      setIsLoading(false); 
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${EDGE_FUNCTION_URL}/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    
    setAffiliate(null);
    setIsLoggedIn(false);
    setEmail("");
    setPassword("");
    setConversions([]);
    setClickCount(0);
  };

  const affiliateLink = `https://roidustreaming.fr/?ref=${affiliate?.referral_code}`;
  
  const copyLink = () => { 
    if (!affiliate) return; 
    navigator.clipboard.writeText(affiliateLink); 
    toast.success("Lien copié dans le presse-papier !"); 
  };
  
  const formatCurrency = (n: number) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n);
  const formatDate = (d: string) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/30">Approuvée</Badge>;
      case "paid":
        return <Badge className="bg-primary/20 text-primary border-primary/30 hover:bg-primary/30">Payée</Badge>;
      case "rejected":
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30">Rejetée</Badge>;
      default:
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 hover:bg-amber-500/30">En attente</Badge>;
    }
  };

  // Stats calculations
  const stats = useMemo(() => {
    const conversionRate = clickCount > 0 ? (conversions.length / clickCount) * 100 : 0;
    const approvedConversions = conversions.filter(c => c.status === "approved" || c.status === "paid").length;
    const pendingConversions = conversions.filter(c => c.status === "pending").length;
    const thisMonth = conversions.filter(c => {
      const date = new Date(c.created_at);
      const now = new Date();
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    });
    const monthlyCommission = thisMonth.reduce((acc, c) => acc + c.commission_amount, 0);
    
    return { conversionRate, approvedConversions, pendingConversions, monthlyCommission, thisMonthCount: thisMonth.length };
  }, [conversions, clickCount]);

  const payoutProgress = Math.min(((affiliate?.pending_earnings || 0) / 50) * 100, 100);

  // Loading state
  if (isLoading && !isLoggedIn) return (
    <>
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20 pt-24 pb-16 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </main>
      <Footer />
    </>
  );

  // Login form
  if (!isLoggedIn) return (
    <>
      <Helmet>
        <title>Dashboard Affilié - Connexion | Roi du Streaming</title>
        <meta name="description" content="Accédez à votre espace affilié pour suivre vos performances et revenus." />
      </Helmet>
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20 pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-primary/60 mb-4">
                <Award className="w-8 h-8 text-primary-foreground" />
              </div>
              <h1 className="text-3xl font-bold mb-2">Espace Affilié</h1>
              <p className="text-muted-foreground">Gérez vos commissions et suivez vos performances</p>
            </div>
            
            <Card className="border-border/50 shadow-xl shadow-primary/5 backdrop-blur-sm">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-xl">Connexion</CardTitle>
                <CardDescription>Entrez vos identifiants pour accéder au dashboard</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                    <Input 
                      id="email" 
                      type="email" 
                      placeholder="votre@email.com" 
                      value={email} 
                      onChange={(e) => setEmail(e.target.value)} 
                      required 
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-medium">Mot de passe</Label>
                    <div className="relative">
                      <Input 
                        id="password" 
                        type={showPassword ? "text" : "password"} 
                        placeholder="••••••••" 
                        value={password} 
                        onChange={(e) => setPassword(e.target.value)} 
                        required 
                        className="h-11 pr-10"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full h-11 font-semibold" disabled={isLoading}>
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                        Connexion...
                      </span>
                    ) : "Se connecter"}
                  </Button>
                </form>
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border/50" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">Nouveau ?</span>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={() => navigate("/affiliate")}
                >
                  Créer un compte affilié
                  <ArrowUpRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );

  const handlePaymentRequest = () => {
    if ((affiliate?.pending_earnings || 0) < 50) {
      toast.error("Minimum de 50€ requis pour demander un paiement.");
      return;
    }
    toast.success("Demande de paiement envoyée ! Vous serez contacté sous 48h.");
  };

  return (
    <>
      <Helmet>
        <title>Dashboard Affilié - {affiliate?.name} | Roi du Streaming</title>
      </Helmet>
      
      <main className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20">
        {/* Top Navigation Bar */}
        <nav className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
                    <Award className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h1 className="font-bold text-lg leading-tight">Espace Affilié</h1>
                    <p className="text-xs text-muted-foreground">Roi du Streaming</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
                  <Zap className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-primary">{affiliate?.commission_rate}% commission</span>
                </div>
                <Button variant="ghost" size="sm" onClick={handleLogout} className="text-muted-foreground hover:text-foreground">
                  <LogOut className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Déconnexion</span>
                </Button>
              </div>
            </div>
          </div>
        </nav>

        <div className="container mx-auto px-4 py-8">
          {/* Welcome Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-1">
                  Bonjour, <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">{affiliate?.name}</span> 👋
                </h2>
                <p className="text-muted-foreground flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Membre depuis le {formatDate(affiliate?.created_at || "")}
                </p>
              </div>
              <Badge variant="outline" className={`self-start ${affiliate?.is_active ? 'border-emerald-500/50 text-emerald-400 bg-emerald-500/10' : 'border-red-500/50 text-red-400 bg-red-500/10'}`}>
                {affiliate?.is_active ? "Compte actif" : "Compte inactif"}
              </Badge>
            </div>
          </div>

          {/* Referral Link Card */}
          <Card className="mb-8 border-primary/20 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <CardContent className="pt-6 relative">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                      <LinkIcon className="w-4 h-4 text-primary" />
                    </div>
                    <span className="font-semibold">Votre lien d'affiliation unique</span>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-background/80 backdrop-blur-sm rounded-lg border border-border/50">
                    <code className="text-sm flex-1 break-all text-foreground/80">
                      {affiliateLink}
                    </code>
                    <Button size="sm" onClick={copyLink} className="shrink-0">
                      <Copy className="w-4 h-4 mr-2" />
                      Copier
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Partagez ce lien pour gagner {affiliate?.commission_rate}% sur chaque vente
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="border-border/50 hover:border-primary/30 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                    <MousePointer className="w-5 h-5 text-blue-400" />
                  </div>
                  <BarChart3 className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-2xl font-bold">{clickCount}</p>
                <p className="text-xs text-muted-foreground mt-1">Clics totaux</p>
              </CardContent>
            </Card>
            
            <Card className="border-border/50 hover:border-primary/30 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-400" />
                  </div>
                  <Target className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-2xl font-bold">{conversions.length}</p>
                <p className="text-xs text-muted-foreground mt-1">Conversions</p>
                <p className="text-xs text-primary mt-0.5">{stats.conversionRate.toFixed(1)}% taux</p>
              </CardContent>
            </Card>
            
            <Card className="border-border/50 hover:border-primary/30 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-primary" />
                  </div>
                  <Wallet className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-2xl font-bold">{formatCurrency(affiliate?.total_earnings || 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">Gains totaux</p>
              </CardContent>
            </Card>
            
            <Card className="border-border/50 hover:border-primary/30 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-400" />
                  </div>
                  <CreditCard className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-2xl font-bold">{formatCurrency(affiliate?.pending_earnings || 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">En attente</p>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Stats + Payout Progress */}
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-primary" />
                    Ce mois-ci
                  </CardTitle>
                  <Badge variant="secondary" className="text-xs">{new Date().toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 rounded-lg bg-muted/50">
                    <p className="text-lg font-bold">{stats.thisMonthCount}</p>
                    <p className="text-xs text-muted-foreground">Ventes</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/50">
                    <p className="text-lg font-bold">{formatCurrency(stats.monthlyCommission)}</p>
                    <p className="text-xs text-muted-foreground">Commission</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/50">
                    <p className="text-lg font-bold">{stats.approvedConversions}</p>
                    <p className="text-xs text-muted-foreground">Approuvées</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-primary" />
                  Prochain paiement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Minimum requis : 50€</span>
                    <span className="font-medium">{formatCurrency(affiliate?.pending_earnings || 0)} / 50€</span>
                  </div>
                  <Progress value={payoutProgress} className="h-2" />
                  <Button 
                    className="w-full" 
                    size="sm"
                    onClick={handlePaymentRequest}
                    disabled={(affiliate?.pending_earnings || 0) < 50}
                  >
                    {(affiliate?.pending_earnings || 0) >= 50 ? (
                      <>Demander le paiement <ExternalLink className="w-4 h-4 ml-2" /></>
                    ) : (
                      `Encore ${formatCurrency(50 - (affiliate?.pending_earnings || 0))} avant le paiement`
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Conversions Table */}
          <Card className="border-border/50">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-primary" />
                  Historique des conversions
                </CardTitle>
                <Badge variant="outline" className="text-xs">
                  {conversions.length} total{conversions.length > 1 ? "es" : ""}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {conversions.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border/50">
                        <TableHead className="text-xs">Date</TableHead>
                        <TableHead className="text-xs">Commande</TableHead>
                        <TableHead className="text-xs text-right">Montant</TableHead>
                        <TableHead className="text-xs text-right">Commission</TableHead>
                        <TableHead className="text-xs text-center">Statut</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {conversions.map((conversion) => (
                        <TableRow key={conversion.id} className="border-border/30 hover:bg-muted/30">
                          <TableCell className="text-sm">{formatDate(conversion.created_at)}</TableCell>
                          <TableCell className="text-sm font-mono text-xs">
                            {conversion.order_id ? `#${conversion.order_id.slice(-8)}` : "—"}
                          </TableCell>
                          <TableCell className="text-sm text-right">{formatCurrency(conversion.order_amount)}</TableCell>
                          <TableCell className="text-sm text-right font-medium text-primary">{formatCurrency(conversion.commission_amount)}</TableCell>
                          <TableCell className="text-center">{getStatusBadge(conversion.status)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4">
                    <Target className="w-8 h-8 text-muted-foreground/50" />
                  </div>
                  <p className="text-muted-foreground font-medium">Aucune conversion pour le moment</p>
                  <p className="text-sm text-muted-foreground/70 mt-1">Partagez votre lien pour commencer à gagner</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </>
  );
};

export default AffiliateDashboard;
